import { useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import { getEddaAccountsByUserId } from "../../../services/userServices";
import { EddaAccountType } from "../../../types/user";
import { QueryKeyEnum } from "../../../types";
import { formatDate } from "../../../helpers/utils";
import ErrorPage from "../../ErrorPage";
import PageLoader from "../../PageLoader";
import UnlinkEddaAccountModal from "../UnlinkEddaAccountModal";

import styles from "./index.module.scss";

interface EddaAccountRowProps {
  account: EddaAccountType;
  /**
   * Whether any row in the table can be unlinked. The column has to be decided
   * for the table as a whole so every row keeps the same number of cells.
   */
  showAction: boolean;
  onUnlink: (account: EddaAccountType) => void;
}

function TableHeader({ count }: { count: number }) {
  return (
    <div className={styles.boxExternalPagination}>
      <span
        className={styles.listGroupTitle}
      >{`eDDA Accounts (${count})`}</span>
    </div>
  );
}

function EddaAccountRow({
  account,
  showAction,
  onUnlink,
}: EddaAccountRowProps) {
  const formatStatus = (statusString?: string) => {
    if (!statusString) return "";
    return statusString
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <tr key={account.id} data-testid={`edda-row-${account.id}`}>
      <td>{account.id}</td>
      <td>{account.bankName}</td>
      <td>{account.accountNumber}</td>
      <td>
        {account.linkedAt
          ? formatDate(account.linkedAt, "d MMM yyyy", "Asia/Singapore")
          : "-"}
      </td>
      <td>
        <span
          className={`${styles.statusBadge} ${
            styles[`status-${account.status?.toLowerCase()}`]
          }`}
        >
          {formatStatus(account.status)}
        </span>
      </td>
      {showAction && (
        <td>
          {account.canUnlink && (
            <Button
              className={styles.unlinkBtn}
              variant="danger"
              size="sm"
              onClick={() => onUnlink(account)}
            >
              Unlink
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}

export default function EddaAccountsTable() {
  const { userId } = useParams();

  const { isLoading, error, data, refetch } = useQuery<
    EddaAccountType[],
    Error
  >(
    [QueryKeyEnum.GetEddaAccounts, userId],
    () => getEddaAccountsByUserId(userId || ""),
    { enabled: Boolean(userId) }
  );

  const [accounts, setAccounts] = useState<EddaAccountType[]>([]);
  const [selectedAccount, setSelectedAccount] =
    useState<EddaAccountType | null>(null);

  useEffect(() => {
    if (data) {
      setAccounts(data);
    }
  }, [data]);

  const onCloseModal = () => setSelectedAccount(null);

  // Nothing in this market or this user's mandates can be unlinked, so the
  // column would be empty for every row.
  const showAction = accounts.some((account) => account.canUnlink);

  if (isLoading)
    return (
      <PageLoader isLoading={isLoading} content="Loading eDDA accounts.." />
    );

  if (error) return <ErrorPage />;

  return (
    <div>
      <TableHeader count={accounts.length} />
      <div className={styles.table}>
        <Table hover bordered responsive="md">
          <thead>
            <tr>
              <th>Mandate ID</th>
              <th>Bank name</th>
              <th>Account number</th>
              <th>Linked on</th>
              <th>Status</th>
              {showAction && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {accounts.length > 0 ? (
              accounts.map((account) => (
                <EddaAccountRow
                  key={account.id}
                  account={account}
                  showAction={showAction}
                  onUnlink={setSelectedAccount}
                />
              ))
            ) : (
              <tr>
                <td colSpan={showAction ? 6 : 5} className={styles.emptyTable}>
                  Oops, no results were found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <UnlinkEddaAccountModal
        isVisible={Boolean(selectedAccount)}
        transactionId={selectedAccount?.transactionId}
        bankName={selectedAccount?.bankName}
        accountNumber={selectedAccount?.accountNumber ?? undefined}
        linkedAt={selectedAccount?.linkedAt}
        status={selectedAccount?.status}
        onClose={onCloseModal}
        onRefetch={refetch}
      />
    </div>
  );
}
